import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme }    from "../context/ThemeContext";
import { useAuth }     from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { Shield, Users, TrendingUp, Briefcase } from "lucide-react";

var RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

var ACCOUNT_TYPES = [
  { id: "client",   label: "Client",          icon: Users,     desc: "Access your trading dashboard and bot portfolio",           roles: ["client","user"] },
  { id: "investor", label: "Investor",         icon: TrendingUp,desc: "Access your investor portal and performance overview",      roles: ["investor"] },
  { id: "agency",   label: "Agency / Partner", icon: Briefcase, desc: "Access your affiliate dashboard and commission reports",    roles: ["affiliate"] },
  { id: "admin",    label: "Admin",            icon: Shield,    desc: "Master admin — full system control across all platforms",   roles: ["admin","ADMIN","SUPER_ADMIN"], restricted: true },
];

function Field(props) {
  var [focused, setFocused] = useState(false);
  var active = focused || (props.value && props.value.length > 0);
  var color  = props.color;
  return (
    <div className="relative">
      <div className="relative border transition-all duration-200"
        style={{
          borderColor: props.error ? "#f87171" : focused ? color : "rgba(255,255,255,0.12)",
          background: "#060606",
          boxShadow: focused && !props.error ? "0 0 0 1px " + color + "25" : "none",
        }}>
        <label className="absolute left-4 pointer-events-none transition-all duration-200 select-none font-mono"
          style={{
            top: active ? "8px" : "50%", transform: active ? "translateY(0)" : "translateY(-50%)",
            fontSize: active ? "9px" : "13px", letterSpacing: active ? "0.12em" : "0.02em",
            textTransform: active ? "uppercase" : "none",
            color: props.error ? "#f87171" : focused ? color : "#52525b",
          }}>
          {props.label}
        </label>
        <input type={props.type || "text"} value={props.value} autoFocus={props.autoFocus}
          onFocus={function () { setFocused(true); }}
          onBlur={function ()  { setFocused(false); }}
          onChange={function (e) { props.onChange(e.target.value); }}
          className="w-full bg-transparent pt-6 pb-2.5 px-4 text-sm text-white outline-none"
          style={{ caretColor: color }} />
      </div>
      {props.error && <p className="mt-1.5 text-xs font-mono text-red-400">! {props.error}</p>}
    </div>
  );
}

function Spinner(props) {
  return (
    <svg className="animate-spin" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="7" stroke={props.color} strokeOpacity="0.25" strokeWidth="2" />
      <path d="M9 2a7 7 0 0 1 7 7" stroke={props.color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function Login(props) {
  var adminMode      = props.adminMode || false;
  var theme          = useTheme().theme;
  var auth           = useAuth();
  var dm             = useDarkMode();
  var isDark         = dm.isDark;
  var navigate       = useNavigate();
  var [searchParams] = useSearchParams();
  var tierParam      = searchParams.get("tier");

  var [accountType, setAccountType] = useState(adminMode ? "admin" : "client");
  var [mode,        setMode]        = useState("login");
  var [mounted,     setMounted]     = useState(false);

  // Form fields
  var [name,       setName]       = useState("");
  var [email,      setEmail]      = useState("");
  var [mobile,     setMobile]     = useState("");
  var [password,   setPassword]   = useState("");
  var [confirmPw,  setConfirmPw]  = useState("");

  // Feedback
  var [loading,     setLoading]     = useState(false);
  var [globalError, setGlobalError] = useState("");
  var [fieldErrors, setFieldErrors] = useState({});
  var [captchaDone, setCaptchaDone] = useState(false);

  var captchaRef = useRef(null);
  var widgetRef  = useRef(null);

  var pageBg      = isDark ? "#030303" : "#FAFAFA";
  var cardBg      = isDark ? "#060606" : "#FFFFFF";
  var border      = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  var textPrimary = isDark ? "#FFFFFF" : "#111111";
  var selectedType = ACCOUNT_TYPES.find(function (t) { return t.id === accountType; }) || ACCOUNT_TYPES[0];
  var accentColor  = selectedType.restricted ? "#E8520F" : theme.color;

  useEffect(function () {
    var t = setTimeout(function () { setMounted(true); }, 60);
    return function () { clearTimeout(t); };
  }, []);

  useEffect(function () {
    if (document.getElementById("recaptcha-script")) return;
    var script   = document.createElement("script");
    script.id    = "recaptcha-script";
    script.src   = "https://www.google.com/recaptcha/api.js?onload=onGLRecaptchaLoad&render=explicit";
    script.async = true;
    window.onGLRecaptchaLoad = function () { renderWidget(); };
    document.head.appendChild(script);
  }, []);

  useEffect(function () {
    if (window.grecaptcha && window.grecaptcha.render && captchaRef.current && widgetRef.current === null) {
      renderWidget();
    }
  });

  function renderWidget() {
    if (!captchaRef.current || widgetRef.current !== null) return;
    try {
      widgetRef.current = window.grecaptcha.render(captchaRef.current, {
        sitekey:            RECAPTCHA_KEY,
        theme:              "dark",
        callback:           function () { setCaptchaDone(true); },
        "expired-callback": function () { setCaptchaDone(false); },
      });
    } catch (e) {}
  }

  useEffect(function () {
    setName(""); setEmail(""); setMobile(""); setPassword(""); setConfirmPw("");
    setGlobalError(""); setFieldErrors({}); setCaptchaDone(false);
    widgetRef.current = null;
    if (window.grecaptcha && captchaRef.current) {
      try { window.grecaptcha.reset(); } catch (e) {}
    }
  }, [mode, accountType]);

  function validate() {
    var errors = {};
    if (mode === "register") {
      if (name.trim().length < 2)  errors.name   = "Name must be at least 2 characters";
      if (!mobile.trim())          errors.mobile = "Mobile number is required";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email    = "Enter a valid email address";
    if (password.length < 8)                        errors.password = "Password must be at least 8 characters";
    if (mode === "register" && password !== confirmPw) errors.confirmPw = "Passwords do not match";
    return errors;
  }

  async function handleSubmit() {
    setGlobalError("");
    var errors = validate();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    if (!captchaDone) { setGlobalError("Please complete the security check."); return; }

    setLoading(true);
    var result;

    if (mode === "login") {
      result = await auth.login(email, password);
    } else {
      var payload = {
        name:     name.trim(),
        email:    email,
        password: password,
        mobile:   mobile.trim(),
      };
      if (tierParam) payload.tier = tierParam;
      result = await auth.register(payload);
    }

    setLoading(false);

    if (!result.ok) {
      setGlobalError(result.error || "Something went wrong. Please try again.");
      setCaptchaDone(false);
      if (widgetRef.current !== null) { try { window.grecaptcha.reset(widgetRef.current); } catch (e) {} }
      return;
    }

    var u = result.user;

    // Route by backend role — never by what tile was selected
    if (u.role === "SUPER_ADMIN" || u.role === "ADMIN" || u.role === "admin") {
      navigate("/admin"); return;
    }
    if (accountType === "admin") {
      // Selected admin tile but role isn't admin
      setGlobalError("This account does not have admin privileges.");
      await auth.logout(); return;
    }
    if (u.role === "affiliate") { navigate("/commander/affiliate"); return; }
    if (u.role === "investor")  { navigate("/investor"); return; }
    if (u.role === "client")    { navigate("/dashboard"); return; }

    // Pending — new signup, needs payment
    if (tierParam) sessionStorage.setItem("gl_pending_tier", tierParam);
    else if (u.tier) sessionStorage.setItem("gl_pending_tier", u.tier);
    navigate("/pending");
  }

  function handleKey(e) { if (e.key === "Enter") handleSubmit(); }

  var isLogin   = mode === "login";
  var tierNames = { tier_6: "Harmony", tier_9: "Power" };
  var tierName  = tierParam ? tierNames[tierParam] : null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: pageBg }} onKeyDown={handleKey}>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full blur-[180px]"
          style={{ background: accentColor, opacity: 0.04 }} />
      </div>
      {isDark && <div className="grain absolute inset-0 opacity-20 pointer-events-none" />}

      <div className="relative z-10 w-full max-w-lg transition-all duration-700 ease-out"
        style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)" }}>

        <Link to="/" className="block text-center font-display font-black text-2xl tracking-tight mb-10"
          style={{ color: accentColor }}>GauriLabs</Link>

        {/* Tier banner */}
        {tierName && mode === "register" && (
          <div className="flex items-center gap-3 px-4 py-3 border mb-5"
            style={{ borderColor: accentColor + "30", background: accentColor + "06" }}>
            <span style={{ color: accentColor }}>&#9670;</span>
            <span className="text-sm" style={{ color: textPrimary }}>
              Signing up for <strong style={{ color: accentColor }}>{tierName}</strong> plan
            </span>
          </div>
        )}

        {/* Account type selector */}
        {!adminMode && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 text-center font-mono">
              Select account type
            </div>
            <div className="grid grid-cols-4 gap-2">
              {ACCOUNT_TYPES.map(function (type) {
                var Icon   = type.icon;
                var active = accountType === type.id;
                var color  = type.restricted ? "#E8520F" : theme.color;
                return (
                  <button key={type.id}
                    onClick={function () { setAccountType(type.id); setMode("login"); }}
                    className="flex flex-col items-center gap-2 py-4 px-2 border transition-all duration-200"
                    style={{
                      borderColor: active ? color : border,
                      background:  active ? color + "0c" : cardBg,
                      boxShadow:   active ? "0 0 20px " + color + "20" : "none",
                    }}>
                    <Icon className="h-4 w-4" style={{ color: active ? color : "#52525b" }} />
                    <span className="font-bold tracking-wide uppercase text-center leading-tight"
                      style={{ color: active ? color : "#71717a", fontSize: "10px" }}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-center text-xs text-zinc-500 mt-3">{selectedType.desc}</p>
          </div>
        )}

        {/* Panel */}
        <div className="border p-8" style={{ background: cardBg, borderColor: selectedType.restricted ? "#E8520F30" : border }}>

          {(adminMode || accountType === "admin") && (
            <div className="flex items-center gap-3 px-4 py-3 border border-[#E8520F]/20 bg-[#E8520F]/5 mb-6">
              <Shield className="h-4 w-4 text-[#E8520F] shrink-0" />
              <span className="text-sm text-[#E8520F] font-mono tracking-wide">Master Admin Access</span>
            </div>
          )}

          {accountType !== "admin" && !adminMode && (
            <div className="flex border mb-7" style={{ borderColor: border }}>
              {["login", "register"].map(function (m) {
                return (
                  <button key={m} onClick={function () { setMode(m); }}
                    className="flex-1 py-3 font-display font-bold text-xs tracking-widest uppercase transition-all"
                    style={{ background: mode === m ? accentColor : "transparent", color: mode === m ? "#000" : "#52525b" }}>
                    {m === "login" ? "Sign In" : "Register"}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mb-7">
            <h1 className="font-display font-black text-2xl tracking-tight" style={{ color: textPrimary }}>
              {accountType === "admin" ? "Admin sign in." : isLogin ? "Welcome back." : "Create your account."}
            </h1>
            <p className="text-zinc-500 text-sm mt-1.5">
              {accountType === "admin"
                ? "Restricted to authorised master admin only."
                : isLogin
                ? "Sign in to your " + selectedType.label.toLowerCase() + " dashboard."
                : "Payment required to activate access."}
            </p>
          </div>

          <div className="space-y-4">
            {mode === "register" && accountType !== "admin" && (
              <Field label="Full Name" value={name} onChange={setName} error={fieldErrors.name} color={accentColor} autoFocus />
            )}
            <Field label="Email Address" type="email" value={email} onChange={setEmail}
              error={fieldErrors.email} color={accentColor} autoFocus={isLogin || accountType === "admin"} />
            {/* Mobile — only on register, required by backend */}
            {mode === "register" && accountType !== "admin" && (
              <Field label="Mobile Number" type="tel" value={mobile} onChange={setMobile}
                error={fieldErrors.mobile} color={accentColor} />
            )}
            <Field label="Password" type="password" value={password} onChange={setPassword}
              error={fieldErrors.password} color={accentColor} />
            {mode === "register" && accountType !== "admin" && (
              <Field label="Confirm Password" type="password" value={confirmPw} onChange={setConfirmPw}
                error={fieldErrors.confirmPw} color={accentColor} />
            )}
          </div>

          {isLogin && (
            <div className="mt-3 text-right">
              <Link to="/forgot-password" className="text-xs font-mono hover:opacity-80 transition-colors"
                style={{ color: accentColor }}>Forgot password?</Link>
            </div>
          )}

          <div className="mt-6 flex justify-center"><div ref={captchaRef} /></div>

          {globalError && (
            <div className="mt-5 flex items-start gap-3 px-4 py-3 border border-red-500/25 bg-red-500/5">
              <span className="text-red-400 text-sm mt-0.5">&#9888;</span>
              <p className="text-red-400 text-sm leading-snug">{globalError}</p>
            </div>
          )}

          <button onClick={handleSubmit} disabled={loading}
            className="w-full mt-7 py-4 font-display font-bold text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-3 disabled:opacity-60 hover:scale-[1.01]"
            style={{ backgroundColor: accentColor, color: "#000", boxShadow: "0 0 32px " + accentColor + "40" }}>
            {loading && <Spinner color="#000" />}
            {loading ? (isLogin ? "Signing in..." : "Creating account...") : (isLogin ? "Sign In" : "Create Account")}
          </button>

          {mode === "register" && accountType !== "admin" && (
            <p className="mt-4 text-center text-xs text-zinc-600">
              After registration you will be directed to complete payment to activate access.
            </p>
          )}

          {accountType !== "admin" && !adminMode && (
            <div className="mt-7 pt-6 border-t text-center" style={{ borderColor: border }}>
              <p className="text-zinc-600 text-xs">
                {isLogin ? "No account? " : "Already registered? "}
                <button onClick={function () { setMode(isLogin ? "register" : "login"); }}
                  className="hover:opacity-80 transition-colors" style={{ color: accentColor }}>
                  {isLogin ? "Register" : "Sign in"}
                </button>
              </p>
            </div>
          )}
        </div>

        {accountType !== "admin" && !adminMode && (
          <div className="mt-6 flex items-center justify-center gap-6">
            <Link to="/apply-investor" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">Investor access</Link>
            <span className="w-1 h-1 rounded-full bg-zinc-700" />
            <Link to="/apply-partner" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">Partner program</Link>
          </div>
        )}
      </div>
    </div>
  );
}
